
import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { formatFullName } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AppNav } from './AppNav';
import type { AppNavItem } from './AppNav';
import { Badge, getRoleBadgeVariant } from '@/components/ui';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const pathname = useLocation().pathname;
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const responsesEnabled = branding.responsesEnabled !== false;
  const stageEnabled = branding.stageEnabled === true;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      setIsCollapsed(saved === 'true');
    }
  }, []);

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', String(newState));
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Build unified navigation items based on user role (always show all accessible items)
  const navItems: AppNavItem[] = useMemo(() => {
    const items: AppNavItem[] = [];

    // Admin gets full access to everything
    if (user?.role === 'admin') {
      items.push({ name: 'Dashboard', href: '/admin', icon: DashboardIcon });
      items.push({ name: 'Submissions', href: '/submissions', icon: PrayersIcon });
      if (stageEnabled) {
        items.push({ name: 'Stage', href: '/stage', icon: StageIcon });
      }
      if (responsesEnabled) {
        items.push({ name: 'Responses', href: '/prayer-partner', icon: VideoCameraIcon });
      }
      items.push({ name: 'Live Queue', href: '/producer', icon: ComputerIcon });
      // Temporarily hidden — restore when Live Display returns
      // items.push({ name: 'Live Display', href: '/live-display', icon: TvIcon, openInNewTab: true });
      items.push({ name: 'Users', href: '/admin/users', icon: UsersIcon });
      items.push({ name: 'Settings', href: '/admin/settings', icon: SettingsIcon });
      items.push({ name: 'Prayer Archive', href: '/admin/archive', icon: ArchiveIcon });
    }
    // Producer gets Live Queue and Submissions
    else if (user?.role === 'producer') {
      items.push({ name: 'Submissions', href: '/submissions', icon: PrayersIcon });
      if (stageEnabled) {
        items.push({ name: 'Stage', href: '/stage', icon: StageIcon });
      }
      items.push({ name: 'Live Queue', href: '/producer', icon: ComputerIcon });
      // Temporarily hidden — restore when Live Display returns
      // items.push({ name: 'Live Display', href: '/live-display', icon: TvIcon, openInNewTab: true });
    }
    // Moderator gets Submissions
    else if (user?.role === 'moderator') {
      items.push({ name: 'Submissions', href: '/submissions', icon: PrayersIcon });
      if (stageEnabled) {
        items.push({ name: 'Stage', href: '/stage', icon: StageIcon });
      }
    }
    // Prayer Partner: queue of prayers sent for video response
    else if (user?.role === 'prayer_partner') {
      if (responsesEnabled) {
        items.push({ name: 'Prayer requests', href: '/prayer-partner', icon: VideoCameraIcon });
      }
    }
    // Congregant: only My Prayers
    else if (user?.role === 'congregant') {
    }

    return items;
  }, [user?.role, responsesEnabled, stageEnabled]);

  const handleLogout = () => {
    logout();
  };

  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';

  const sidebarLogoHref = useMemo(() => {
    if (pathname.startsWith('/admin')) return '/admin';
    if (pathname.startsWith('/producer')) return '/producer';
    if (pathname.startsWith('/prayer-partner')) return '/prayer-partner';
    if (pathname.startsWith('/stage')) return '/stage';
    if (pathname.startsWith('/submissions')) return '/submissions';
    return '/submissions';
  }, [pathname]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transform transition-all lg:translate-x-0',
          sidebarWidth,
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo and collapse toggle */}
          <div className="h-16 flex items-center border-b border-slate-200 dark:border-slate-700 relative">
            {!isCollapsed ? (
              <>
                <Link to={sidebarLogoHref} className="flex items-center gap-2 px-4">
                  {branding.appLogoUrl ? (
                    <img
                      src={branding.appLogoUrl}
                      alt={`${branding.appName} logo`}
                      className="h-8 max-w-[160px] w-auto object-contain bg-white"
                    />
                  ) : (
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {branding.appName}
                    </span>
                  )}
                </Link>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden absolute right-4 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  <CloseIcon className="w-6 h-6" />
                </button>
                <button
                  onClick={toggleCollapsed}
                  className="hidden lg:block absolute right-4 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  title="Collapse sidebar"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </>
            ) : (
              <div className="w-full flex flex-col items-center justify-center py-2">
                <Link to={sidebarLogoHref}>
                  {(branding.appIconUrl ?? branding.appLogoUrl) ? (
                    <img
                      src={branding.appIconUrl ?? branding.appLogoUrl ?? ''}
                      alt={`${branding.appName} icon`}
                      className="w-8 h-8 object-contain bg-white"
                      title={branding.appName}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">O</span>
                    </div>
                  )}
                </Link>
                <button
                  onClick={toggleCollapsed}
                  className="hidden lg:block mt-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                  title="Expand sidebar"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex-1 p-4 overflow-y-auto">
            <AppNav items={navItems} pathname={pathname} isCollapsed={isCollapsed} />
          </div>

          {/* User info footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-4">
            {isCollapsed ? (
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400" title={formatFullName(user?.firstName, user?.lastName) || user?.email || 'User'}>
                  <span className="text-sm font-medium">
                    {formatFullName(user?.firstName, user?.lastName)?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  title="Sign out"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                  <span className="text-sm font-medium">
                    {formatFullName(user?.firstName, user?.lastName)?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                    {formatFullName(user?.firstName, user?.lastName) || user?.email || 'User'}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400 capitalize">
                    {user?.role ?? ''}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer"
                  title="Sign out"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('transition-all', isCollapsed ? 'lg:pl-16' : 'lg:pl-64')}>
        {/* Mobile only: slim bar with menu button */}
        <div className="lg:hidden h-12 flex items-center px-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Open menu"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}

// Icon components
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ArchiveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );
}

function PrayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ComputerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25"
      />
    </svg>
  );
}

// Kept for Live Display nav restore (sidebar items temporarily commented out).
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- restore with Live Display nav
function TvIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 7.5A2.25 2.25 0 0 1 5.25 5.25h13.5A2.25 2.25 0 0 1 21 7.5v8.25a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15.75V7.5Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.25 21h7.5"
      />
    </svg>
  );
}

function StageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 19h16M6 19V9h12v10M9 9V6h6v3"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 12v3"
      />
    </svg>
  );
}

function VideoCameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25Z"
      />
    </svg>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
